import {
  buildGetConsentRequest,
  getStoreConsentMutationRequest
} from "@/vue/services/cmp/CmpConsentBackendServiceGraphQLRequests";
import { CmpService } from "@/vue/services/cmp/CmpService";

export type GraphQLAuthentication = {
  registrationId: string;
  computedHmac: string;
  passPhrase: string;
};

type ConsentData = {
  controllerId: string;
  uCConfigurationId: string;
  createdAt: string;
  modifiedAt: string | undefined;
};
export type ConsentDecisions = {
  code: string;
  name: string;
  selected: boolean;
};

export type ConsentInput = {
  registrationId?: string;
  customerId?: string;
  accountId?: string;
  uCControllerId: {
    controllerId: string;
    uCConfigurationId: string;
  };
  consents?: ConsentDecisions[];
};

function resolveConsentBackendHost() {
  return window.location.hostname.startsWith("www.bestsecret.")
    ? "https://consent-api-prod.bestsecret.com/public/graphql"
    : "https://consent-backend.internal.dev.euwest.azure.bestsecrettec.com/public/graphql";
}

function resolveConsentBackendUrl(isPublic: boolean = false): string {
  //If there is no customerId we should use public endpoint, otherwise, use GraphQL gateway
  return isPublic ? resolveConsentBackendHost() : "/bff-spa";
}

export interface CmpConsentBackendService {
  storeConsentInConsentBackendService(
    authentication: GraphQLAuthentication,
    consentInput: ConsentInput
  ): Promise<ConsentData | void>;

  getConsentFromConsentBackendService(
    authentication: GraphQLAuthentication
  ): Promise<ConsentData | void>;

  storeConsentInConsentBackendServiceForLoggedInUsers(
    consentInput: ConsentInput
  ): Promise<ConsentData | void>;

  getConsentFromConsentBackendServiceForLoggedInUsers(): Promise<ConsentData | void>;
}

export class CmpConsentBackendServiceImpl implements CmpConsentBackendService {
  private consentBackendEndpoint: string;
  private cmpService: CmpService;
  private accountId: string | undefined;
  private shouldInformConsentBackend: boolean;

  constructor(
    cmpService: CmpService,
    shouldInformConsentBackend: boolean,
    accountId?: string
  ) {
    // Maybe it´s needed to check more than one hostname patterns to detect PROD TLD´s (.at .nl ...) and other pages as jobs.bestsecret.com (not needed by now)
    this.accountId = accountId;
    this.consentBackendEndpoint = resolveConsentBackendUrl(
      accountId === undefined ||
        window.location.pathname.startsWith("/acquisition") // need to check if we are still on ACQ domain
    );
    this.cmpService = cmpService;
    this.shouldInformConsentBackend = shouldInformConsentBackend;
  }

  public async storeConsentInConsentBackendService(
    authentication: GraphQLAuthentication,
    consentInput: ConsentInput
  ): Promise<ConsentData | void> {
    const body = getStoreConsentMutationRequest(
      consentInput,
      this.cmpService.getAllDecisions()
    );

    return this.invokeConsentBackend(body, authentication);
  }

  public async getConsentFromConsentBackendService(
    authentication: GraphQLAuthentication
  ): Promise<ConsentData | void> {
    const body = buildGetConsentRequest(
      authentication.registrationId,
      undefined
    );
    return this.invokeConsentBackend(body, authentication);
  }

  public async storeConsentInConsentBackendServiceForLoggedInUsers(
    consentInput: ConsentInput
  ): Promise<ConsentData | void> {
    if (this.shouldInformConsentBackend) {
      const body = getStoreConsentMutationRequest(
        consentInput,
        this.cmpService.getAllDecisions()
      );

      return this.invokeConsentBackend(body, undefined);
    }
  }

  public async getConsentFromConsentBackendServiceForLoggedInUsers(): Promise<ConsentData | void> {
    if (this.shouldInformConsentBackend) {
      const body = buildGetConsentRequest(undefined, this.accountId);
      return this.invokeConsentBackend(body, undefined);
    }
  }

  private async invokeConsentBackend(
    body: string,
    authentication?: GraphQLAuthentication
  ): Promise<ConsentData | void> {
    const fetchInitObject: RequestInit = {
      method: "POST",
      ...this.buildInvocationHeaders(authentication),
      body: JSON.stringify({ query: body })
    };

    return fetch(this.consentBackendEndpoint, fetchInitObject)
      .then(response => {
        if (response && response.ok) {
          return response.json();
        }
      })
      .then((
        json // depending on the request, the response will be different
      ) =>
        json.data["storeConsent"] === undefined
          ? json.data["getConsent"]
          : json.data["storeConsent"]
      )
      .catch(error => {
        if (error != null) {
          console.error(
            `Could not perform consent-BE request: ${JSON.stringify(
              error,
              Object.getOwnPropertyNames(error)
            )}`
          );
        } else {
          console.error(
            `Could not perform consent-BE request. Error was null.`
          );
        }
      });
  }

  private buildInvocationHeaders(authentication?: GraphQLAuthentication) {
    const headers: Record<string, string> = {
      "Content-Type": "application/json"
    };

    if (authentication != undefined) {
      headers["X-ID"] = `${authentication.registrationId}`;
      headers["X-HMAC"] = `${authentication.computedHmac}`;
      headers["X-HMAC-KEY"] = `${authentication.passPhrase}`;
    }

    return { headers };
  }
}
